import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { ErrorBoundary } from '@shared/ui';
import { salesRoutes } from '@sales/app/routes/routeTree';
import { opsRoutes } from '@ops/app/routes/routeTree';
import { catalogRoutes } from '@catalog/app/routes/routeTree';
import { rfpRoutes } from '@rfp/app/routes/routeTree';
import { mapleRoutes } from '@maple/app/routes/routeTree';
import { ideasRoutes } from '@ideas/app/routes/routeTree';
import { marketingRoutes } from '@marketing/app/routes/routeTree';
import { financeRoutes } from '@finance/app/routes/routeTree';
import { academyRoutes } from '@academy/app/routes/routeTree';
import { portfolioRoutes } from '@portfolio/app/routes/routeTree';
import { shareRoutes } from '@share/app/routes/routeTree';
import { deskRoutes } from '@desk/app/routes/routeTree';
import { rxRoutes } from '@rx/app/routes/routeTree';
import { analyticsRoutes } from '@analytics/app/routes/routeTree';
import { ShellLayout } from '../ShellLayout';
// Imported from the file, not the barrel: a static import of the barrel would
// keep every Central page in the main chunk and defeat the lazy() calls below.
import { HomePage } from '@pages/home/HomePage';

// Every page below the home page loads on demand, the way each module's
// routeTree lazy-loads its pages. Until 10/11 they were all static imports,
// which put the 1,500-line Admin page, the Spotlight admin, Central analytics
// and User Activity into the main chunk every visitor downloads before the
// first paint, for pages a handful of people open.
const ShoutOutsPage = lazy(() => import('@pages/home').then((m) => ({ default: m.ShoutOutsPage })));
const AwardsPage = lazy(() => import('@pages/home').then((m) => ({ default: m.AwardsPage })));
const MascotAdminPage = lazy(() => import('@pages/home').then((m) => ({ default: m.MascotAdminPage })));
const CentralAdminPage = lazy(() => import('@pages/home').then((m) => ({ default: m.CentralAdminPage })));
const CentralAnalyticsPage = lazy(() => import('@pages/home').then((m) => ({ default: m.CentralAnalyticsPage })));
const CentralFeaturesPage = lazy(() => import('@pages/home').then((m) => ({ default: m.CentralFeaturesPage })));
const SpotlightAdminPage = lazy(() => import('@pages/home').then((m) => ({ default: m.SpotlightAdminPage })));
const ThisOrThatPage = lazy(() => import('@pages/home').then((m) => ({ default: m.ThisOrThatPage })));
const ThisOrThatResultsPage = lazy(() => import('@pages/home').then((m) => ({ default: m.ThisOrThatResultsPage })));
const AdminLayout = lazy(() => import('@pages/admin').then((m) => ({ default: m.AdminLayout })));
const AdminPage = lazy(() => import('@pages/admin').then((m) => ({ default: m.AdminPage })));
const AuditLogPage = lazy(() => import('@pages/admin').then((m) => ({ default: m.AuditLogPage })));
const DemoPagesPage = lazy(() => import('@pages/admin').then((m) => ({ default: m.DemoPagesPage })));
const BroadcastsPage = lazy(() => import('@pages/admin').then((m) => ({ default: m.BroadcastsPage })));
const AiModelsPage = lazy(() => import('@pages/admin').then((m) => ({ default: m.AiModelsPage })));
const GeocodingPage = lazy(() => import('@pages/admin').then((m) => ({ default: m.GeocodingPage })));
const UserActivityPage = lazy(() => import('@pages/user-activity').then((m) => ({ default: m.UserActivityPage })));

function RootLayout() {
  return (
    <ErrorBoundary>
      <ShellLayout>
        <Suspense fallback={<Spinner size="small" label="Loading..." style={{ padding: '24px' }} />}>
          <Outlet />
        </Suspense>
      </ShellLayout>
    </ErrorBoundary>
  );
}

/**
 * RFP was renamed Proposal (display + route only, 2026-08-07): every old
 * `/rfp/...` link keeps working, subpath, query, and hash intact. The module
 * package, its role values, and `/api/rfp/*` are unchanged.
 */
function LegacyRfpRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/rfp/, '/proposal')}${search}${hash}`} replace />;
}

function LegacyClinicalRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/clinical/, '/analytics')}${search}${hash}`} replace />;
}

/**
 * Wired modules supply their own AppShell chrome, so we mount them directly
 * without the shell's `ShellLayout` wrapper. The launcher keeps the shell
 * chrome.
 */
export const router = createBrowserRouter([
  {
    path: 'sales/*',
    children: salesRoutes('/sales'),
  },
  {
    path: 'ops/*',
    children: opsRoutes('/ops'),
  },
  {
    path: 'catalog/*',
    children: catalogRoutes('/catalog'),
  },
  {
    path: 'proposal/*',
    children: rfpRoutes('/proposal'),
  },
  { path: 'rfp/*', element: <LegacyRfpRedirect /> },
  {
    path: 'maple/*',
    children: mapleRoutes('/maple'),
  },
  {
    path: 'ideas/*',
    children: ideasRoutes('/ideas'),
  },
  {
    path: 'marketing/*',
    children: marketingRoutes('/marketing'),
  },
  {
    path: 'finance/*',
    children: financeRoutes('/finance'),
  },
  {
    path: 'academy/*',
    children: academyRoutes('/academy'),
  },
  {
    path: 'portfolio/*',
    children: portfolioRoutes('/portfolio'),
  },
  {
    path: 'share/*',
    children: shareRoutes('/share'),
  },
  {
    path: 'desk/*',
    children: deskRoutes('/desk'),
  },
  {
    path: 'rx/*',
    children: rxRoutes('/rx'),
  },
  {
    path: 'analytics/*',
    children: analyticsRoutes('/analytics'),
  },
  // Renamed Clinical -> Analytics 9/04, one day into local use: the redirect
  // costs three lines and spares anyone's open tab or bookmark.
  { path: 'clinical/*', element: <LegacyClinicalRedirect /> },
  {
    element: <RootLayout />,
    children: [
      // Vera Home + Veraciter Central (docs/design/home/PLAN.md).
      { path: '/', element: <HomePage /> },
      // Veraciter Central's own pages, open to every signed-in user. Central
      // itself is the panel on the home page, so a bare /central goes there.
      { path: '/central', element: <Navigate to="/" replace /> },
      { path: '/central/shout-outs', element: <ShoutOutsPage /> },
      { path: '/central/admin', element: <CentralAdminPage /> },
      { path: '/central/this-or-that', element: <ThisOrThatPage /> },
      { path: '/central/this-or-that/results', element: <ThisOrThatResultsPage /> },
      { path: '/central/spotlight', element: <SpotlightAdminPage /> },
      { path: '/central/features', element: <CentralFeaturesPage /> },
      { path: '/central/analytics', element: <CentralAnalyticsPage /> },
      { path: '/central/awards', element: <AwardsPage /> },
      { path: '/central/mascot', element: <MascotAdminPage /> },
      // Guide retired into Academy Know (PLAN 11 step 3): redirects preserve
      // the old bookmarks; content management moved to /academy/authoring.
      { path: '/guide', element: <Navigate to="/academy/know" replace /> },
      {
        path: '/admin',
        element: <AdminLayout />,
        children: [
          // User Activity is the default admin tab, so it owns the index
          // route; Roles moved to /admin/roles and /admin/activity redirects
          // so existing bookmarks and links still land.
          { index: true, element: <UserActivityPage /> },
          { path: 'activity', element: <Navigate to="/admin" replace /> },
          { path: 'roles', element: <AdminPage /> },
          { path: 'audit', element: <AuditLogPage /> },
          { path: 'guide', element: <Navigate to="/academy/authoring" replace /> },
          { path: 'demos', element: <DemoPagesPage /> },
          { path: 'broadcasts', element: <BroadcastsPage /> },
          { path: 'ai-models', element: <AiModelsPage /> },
          { path: 'geocoding', element: <GeocodingPage /> },
        ],
      },
    ],
  },
]);
